import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useCart } from '../context/CartContext';
import useContactSettings from '../hooks/useContactSettings';
import { formatNumber } from '../utils/formatting';
import './Footer.css';

const Footer = () => {
  const { t, language } = useTranslation();
  const { contactSettings } = useContactSettings();
  const { getCartItemCount } = useCart();
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const cartItemCount = getCartItemCount();
  
  const whatsappNumber = (contactSettings?.whatsapp || '+20 12 29009073').replace(/\D/g, '');
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;
  const phoneNumber = contactSettings?.phone || '+20 12 29009073';
  const phoneTelHref = `tel:${phoneNumber.replace(/\D/g, '')}`;
  const email = contactSettings?.email || 'mohamedhassanamin.co@gmail.com';
  const locationAddress = contactSettings?.location_address || '';
  const locationMapUrl = contactSettings?.location_map_url || 'https://maps.app.goo.gl/eLfVkqokJQxiXmyY8?g_st=iw';
  const instagramUrl = contactSettings?.instagram_url || '';
  const facebookUrl = contactSettings?.facebook_url || 'https://www.facebook.com';
  const creditName = contactSettings?.credit_name || 'Ektra Tech';
  const creditNameAr = contactSettings?.credit_name_ar || contactSettings?.credit_name || 'إكترا';
  const creditUrl = contactSettings?.credit_url || 'https://ektra-eg.com';

  const handleInfoClick = (e) => {
    e.preventDefault();
    if (popupOpen) {
      handleClosePopup(e);
    } else {
      setPopupOpen(true);
    }
  };

  const handleClosePopup = useCallback((e) => {
    if (e) e.preventDefault();
    setPopupVisible(false);
    setTimeout(() => setPopupOpen(false), 200);
  }, []);

  useEffect(() => {
    if (popupOpen) {
      requestAnimationFrame(() => {
        setPopupVisible(true);
      });
    }
  }, [popupOpen]);

  return (
    <>
      <footer className="footer-area" style={{ backgroundColor: '#30766b', color: '#fff' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="footer-content py-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                <div className="footer-credits">
                  <p className="mb-2">
                    {language === 'ar' ? (
                      <>
                        {t.companyName} © {formatNumber(1972, language)}. {t.allRightsReserved}
                      </>
                    ) : (
                      <>
                        © {formatNumber(1972, language)} {t.companyName}. {t.allRightsReserved}
                      </>
                    )}
                  </p>
                  <p className="mb-0">
                    {t.designedBy}{' '}
                    <a 
                      href={creditUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="credit-link"
                    >
                      <strong>{language === 'ar' ? creditNameAr : creditName}</strong>
                    </a>
                  </p>
                  <p className="mb-0" style={{ marginTop: '8px', fontSize: '12px', opacity: 0.9 }}>
                    {t.taxNumber}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {cartItemCount > 0 && (
        <Link 
          to="/cart"
          className="cart-float"
          aria-label="Shopping Cart"
        >
          <i className="la la-shopping-cart"></i>
          <span className="cart-float-badge">{cartItemCount}</span>
        </Link>
      )}
      
      <button 
        className="info-float"
        onClick={handleInfoClick}
        aria-label="Company Information"
      >
        <i className="la la-info-circle"></i>
      </button>
      
      <a 
        href={whatsappUrl}
        target="_blank" 
        rel="noopener noreferrer" 
        className="whatsapp-float"
        aria-label="WhatsApp"
      >
        <i className="la la-whatsapp"></i>
      </a>
      
      {popupOpen && (
        <>
          <div className={`info-popup-overlay ${popupVisible ? 'visible' : ''}`} onClick={handleClosePopup}></div>
          <div className={`info-popup ${popupVisible ? 'visible' : ''}`}>
            <button className="info-popup-close" onClick={handleClosePopup}>
              <i className="la la-times"></i>
            </button>
            <div className="info-popup-content">
              <h3>{t.contactUs}</h3>
              <div className="info-popup-details">
                <div className="info-popup-item">
                  <i className="la la-map-marker"></i>
                  <a 
                    href={locationMapUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {t.companyAddress}<br />
                    {t.companyCityCountry}
                  </a>
                </div>
                <div className="info-popup-item">
                  <i className="la la-phone"></i>
                  <a href={phoneTelHref} dir="ltr">{phoneNumber}</a>
                </div>
                <div className="info-popup-item">
                  <i className="la la-envelope"></i>
                  <a href={`mailto:${email}`} dir="ltr">{email}</a>
                </div>
              </div>
              <div className="info-popup-social">
                {facebookUrl && (
                  <a 
                    href={facebookUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="popup-social-icon facebook"
                    aria-label="Facebook"
                  >
                    <i className="la la-facebook"></i>
                  </a>
                )}
                {instagramUrl && (
                  <a 
                    href={instagramUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="popup-social-icon instagram"
                    aria-label="Instagram"
                  >
                    <i className="la la-instagram"></i>
                  </a>
                )}
                {whatsappUrl && (
                  <a 
                    href={whatsappUrl}
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="popup-social-icon whatsapp"
                    aria-label="WhatsApp"
                  >
                    <i className="la la-whatsapp"></i>
                  </a>
                )}
                {locationMapUrl && (
                  <a 
                    href={locationMapUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="popup-social-icon google-maps"
                    aria-label="Location"
                  >
                    <i className="la la-map-marker"></i>
                  </a>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Footer;
